import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Send, ShieldCheck, UserPlus, X } from 'lucide-react'
import { useSuperAdmin } from '@/hooks/useSuperAdmin'
import { useStudioStore } from '@/store'
import {
  cancelAccessRequest,
  createAccessRequest,
  fetchMyAccessRequests,
  type AccessRequestStatus,
  type ManagementAccessLevel,
  type MyAccessRequest,
} from '@/services/managementAccessService'

/**
 * Заявка на доступ в управляющую компанию.
 *
 * Решение владельца 30.08.2026: выдаёт доступ только супер-админ (3 человека),
 * все остальные сотрудники УК — включая advanced — оставляют заявку здесь, а
 * решения принимаются в разделе «Пользователи».
 *
 * ⚠️ Экран открыт сотруднику УК ЛЮБОГО уровня, и это его смысл: у standard
 * кнопок не было никогда, ради него заявка и заведена. Настоящий гейт —
 * серверный (`management_access_actor()` внутри RPC), здесь только форма.
 *
 * Замер PROD 30.08.2026, из-за которого сужение сделано именно так: за 90 дней
 * инвайты УК создавали 8 человек, 5 из них не супер-админы (17 инвайтов из 38).
 * Эти пятеро не теряют возможность начать — они теряют самообслуживание.
 */

const STATUS_LABELS: Record<AccessRequestStatus, string> = {
  PENDING: 'На рассмотрении',
  APPROVED: 'Одобрена',
  REJECTED: 'Отклонена',
  CANCELLED: 'Отозвана',
}

const STATUS_CLASSES: Record<AccessRequestStatus, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
  CANCELLED: 'bg-muted text-muted-foreground border-border',
}

const LEVEL_LABELS: Record<ManagementAccessLevel, string> = {
  standard: 'Стандартный — просмотр данных УК и франчайзи',
  advanced: 'Расширенный — для руководителей',
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const EMPTY_FORM = {
  email: '',
  firstName: '',
  lastName: '',
  position: '',
  reason: '',
  accessLevel: 'standard' as ManagementAccessLevel,
}

function submitErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : ''
  if (message.includes('У этого человека уже есть действующий доступ в УК')) {
    return 'Сотрудник уже добавлен в систему и имеет действующий доступ в УК. Повторно отправлять приглашение не нужно. Проверьте его карточку сотрудника.'
  }
  if (message.includes('Заявка на этот адрес уже подана и ещё не рассмотрена')) {
    return 'Приглашение этому сотруднику уже отправлено и ожидает рассмотрения. Повторно отправлять заявку не нужно.'
  }
  return 'Не удалось отправить заявку. Попробуйте ещё раз. Если ошибка повторяется, обратитесь к администратору.'
}

export const ManagementAccessPage: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { studios } = useStudioStore()
  const { isSuperAdmin } = useSuperAdmin()
  const [form, setForm] = useState(EMPTY_FORM)
  const [error, setError] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState<string | null>(null)

  const isManagementCompany = studios.some(
    studio => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )

  const { data: myRequests = [], isLoading } = useQuery({
    queryKey: ['my-access-requests'],
    queryFn: () => fetchMyAccessRequests(),
    enabled: isManagementCompany,
  })

  const submit = useMutation({
    mutationFn: () => createAccessRequest(form),
    onSuccess: () => {
      setSentTo(form.email.trim().toLowerCase())
      setForm(EMPTY_FORM)
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['my-access-requests'] })
    },
    onError: (e: unknown) => setError(submitErrorMessage(e)),
  })

  const cancel = useMutation({
    mutationFn: (id: number) => cancelAccessRequest(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['my-access-requests'] }),
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Не удалось отозвать заявку'),
  })

  const isValid = useMemo(
    () =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
      form.firstName.trim().length > 0 &&
      form.lastName.trim().length > 0 &&
      form.position.trim().length > 0,
    [form]
  )

  if (!isManagementCompany) {
    return (
      <div className="container mx-auto max-w-2xl p-4">
        <div className="rounded-md border border-border bg-card p-6 text-center">
          <h1 className="text-lg font-semibold text-foreground">Доступ в УК</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Заявку на доступ может оставить только действующий сотрудник управляющей компании.
          </p>
        </div>
      </div>
    )
  }

  const field = (key: keyof typeof EMPTY_FORM, label: string, placeholder: string) => (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <input
        value={form[key] as string}
        onChange={e => {
          setForm(prev => ({ ...prev, [key]: e.target.value }))
          setError(null)
        }}
        placeholder={placeholder}
        className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-slate-400"
      />
    </label>
  )

  return (
    <div className="container mx-auto max-w-3xl p-4 pb-24">
      <div className="mb-5">
        <div className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-bold text-foreground">Заявка на доступ в УК</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Чтобы открыть сотруднику доступ к порталу, укажите его ФИО, должность и рабочую почту. При необходимости оставьте дополнительный комментарий. Заявка попадет администратору и после одобрения пользователю придет письмо со ссылкой.
        </p>
      </div>

      {isSuperAdmin === true && (
        <button
          type="button"
          onClick={() => navigate('/management/users')}
          className="mb-4 w-full text-left"
        >
          <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3 transition-colors hover:bg-accent/50">
            <div className="rounded-lg bg-primary/10 p-2 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-sm font-semibold">Вы выдаёте доступы</span>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Заявки, приглашения и сотрудники УК — в разделе «Пользователи»
              </p>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </div>
        </button>
      )}

      {sentTo && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          Заявка на <b>{sentTo}</b> отправлена. Как только её рассмотрят, исход появится ниже.
        </div>
      )}

      <div className="rounded-md border border-border bg-card p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {field('lastName', 'Фамилия', 'Иванова')}
          {field('firstName', 'Имя', 'Мария')}
          {field('email', 'Рабочая почта', 'name@levitafamily.ru')}
          {field('position', 'Должность', 'Бухгалтер')}
        </div>

        <label className="mt-3 block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Уровень доступа</span>
          <select
            value={form.accessLevel}
            onChange={e => setForm(prev => ({ ...prev, accessLevel: e.target.value as ManagementAccessLevel }))}
            className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm outline-none focus:border-slate-400"
          >
            {(Object.keys(LEVEL_LABELS) as ManagementAccessLevel[]).map(level => (
              <option key={level} value={level}>
                {LEVEL_LABELS[level]}
              </option>
            ))}
          </select>
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">
            Дополнительный комментарий <span className="font-normal">(необязательно)</span>
          </span>
          <textarea
            value={form.reason}
            onChange={e => {
              setForm(prev => ({ ...prev, reason: e.target.value }))
              setError(null)
            }}
            rows={3}
            maxLength={1000}
            placeholder="Оставьте дополнительный комментарий при необходимости"
            className="w-full rounded-md border border-border bg-background p-2 text-sm outline-none focus:border-slate-400"
          />
        </label>

        {error && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700">{error}</div>
        )}

        <button
          type="button"
          disabled={!isValid || submit.isPending}
          onClick={() => submit.mutate()}
          className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          {submit.isPending ? 'Отправляем…' : 'Отправить заявку'}
        </button>
      </div>

      <div className="mt-6">
        <h2 className="mb-2 text-sm font-semibold text-foreground">Мои заявки</h2>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Загрузка…</p>
        ) : myRequests.length === 0 ? (
          <p className="text-sm text-muted-foreground">Вы ещё не подавали заявок.</p>
        ) : (
          <div className="space-y-2">
            {myRequests.map((request: MyAccessRequest) => (
              <div key={request.id} className="rounded-md border border-border bg-card p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">
                      {request.target_last_name} {request.target_first_name} — {request.target_position}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {request.target_email} · подана {formatDateTime(request.created_at)}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 rounded-full border px-2 py-0.5 text-xs ${STATUS_CLASSES[request.status]}`}
                  >
                    {STATUS_LABELS[request.status]}
                  </span>
                </div>

                {request.resolution_note && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Комментарий: {request.resolution_note}
                  </div>
                )}

                {request.status === 'PENDING' && (
                  <button
                    type="button"
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate(request.id)}
                    className="mt-2 inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-xs text-muted-foreground hover:bg-muted"
                  >
                    <X className="h-3.5 w-3.5" />
                    Отозвать
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
